import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import AdServerDetails from "../components/job/AdServerDetails";
import { EmptyTasksAlert } from "../components/job/JobAlerts";
import JobDetailsView from "../components/job/JobDetails";
import JobFiles from "../components/job/JobFiles";
import PageContainer from "../components/shared/PageContainer";
import TasksTable from "../components/task/TasksTable";
import { useAlert } from "../contexts/AlertContext";
import { useAuth } from "../contexts/AuthContext";
import { useAutoUpdate } from "../contexts/AutoUpdateContext";
import { useConfig } from "../contexts/ConfigContext";
import * as Objects from "../services/api";
import { UserRole } from "../services/api/AuthUser";
import { OperationType } from "../services/api/JobOperation";
import { Files, FilesType } from "../services/api/files";

const JobDetails: React.FC = () => {
  const { currentUser } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<Objects.Job | null>();
  const [files, setFiles] = useState<FilesType | null>();
  const [path, setPath] = useState<string | null>();
  const [availableProfiles, setAvailableProfiles] = useState<Objects.Profile[]>([]);
  const [availableSets, setAvailableSets] = useState<Objects.TemplateSetAggregate[]>([]);

  const autoUpdate = useAutoUpdate();
  const { config } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { alert } = useAlert();

  useEffect(() => {
    // profiles are used for retrying jobs which cannot be done by guest users
    if ([UserRole.ADMIN, UserRole.OPERATOR].includes(currentUser.role)) {
      Objects.Profiles.list().then(setAvailableProfiles);
    }
    Objects.TemplateSets.list().then(setAvailableSets);
  }, [currentUser]);

  useEffect(() => {
    Objects.Jobs.retrieve(id!).then((job) => {
      setJob(job);
      // TODO: Make config context and read output_bucket_prefix from config.
      setPath(`atp/${job.creative_id}/`);
    });

    const fetchData = () => Objects.Jobs.retrieve(id!).then(setJob);
    let intervalId = autoUpdate.active ? setInterval(fetchData, 5000) : null;
    return () => (intervalId ? clearInterval(intervalId) : undefined);
  }, [id, autoUpdate]);

  useEffect(() => {
    if (path && currentUser.role === UserRole.ADMIN) Files.retrieve(path).then(setFiles);
  }, [path, currentUser.role]);

  const hasFiles = path && (files?.files || files?.folders);

  const handleReTranscodeClick = async () => {
    navigate("/jobs/retranscode", { state: { from: location.pathname, ids: [id] } });
  };

  const handleRegisterClick = async () => {
    await new Objects.JobOperation({
      ids: [job!._id],
      accounts: config.registerAccounts,
      primary_regions: config.regions.primary_regions,
      secondary_regions: config.regions.secondary_regions,
      overwrite: true,
      useDefaultProfiles: true,
      profiles: [],
      operation: OperationType.REGISTER,
    }).create();
  };

  const handleRetryClick = async () => {
    const profilesForRetry = Objects.JobOperation.getProfilesForRetry(job!, availableProfiles, availableSets);

    // if no profiles are found, throw an error and do not retry
    if (profilesForRetry.length === 0) {
      alert.error("No profiles available to retry the job. Please check the profiles and try again.");
      return;
    }

    await Objects.JobOperation.retry(
      job!.id,
      profilesForRetry,
      config.registerAccounts,
      config.regions.primary_regions,
      config.regions.secondary_regions,
    );
  };

  return (
    <PageContainer>
      {job && (
        <JobDetailsView
          job={job}
          availableSets={availableSets}
          onRegisterClick={handleRegisterClick}
          onReTranscodeClick={handleReTranscodeClick}
          onRetryClick={handleRetryClick}
        />
      )}
      {job?.tasks ? <TasksTable tasks={job.tasks} /> : <EmptyTasksAlert />}
      <AdServerDetails request={job?.ad_server_request} response={job?.ad_server_response} />
      {hasFiles && <JobFiles files={files} path={path} setPath={setPath} />}
    </PageContainer>
  );
};

export default JobDetails;
